import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import RiskStratificationService from "../service/RiskStratificationService";
import ChiefComplaintsService from "../sections/chiefComplaints/ChiefComplaintsService";
import DiagnosisService from "../sections/diagnosis/DiagnosisService";
import HospitalisationService from "../sections/hospitalisation/HospitalisationService";
import ActivityService from "../sections/physicalActivity/ActivityService";
import { CC_V2_REFRESH } from "../sections/chiefComplaints/events";
import { DX_V2_REFRESH } from "../sections/diagnosis/events";
import { HOSP_V2_REFRESH } from "../sections/hospitalisation/events";
import { ACT_V2_REFRESH } from "../sections/physicalActivity/events";
import AuthenticationService from "service/auth/AuthenticationService";

import "../styles/patientOverview.scss";

/**
 * Patient health overview — the left card of the overview row. Source: Figma
 * node 2428:4821 "ALT B · Patient health overview (ghost chips)", read via Dev
 * Mode MCP. Card is 10px radius / 20px-16px padding, not the 12px $radius the
 * section cards use — that's the board's value, not a slip.
 *
 * The risk pill + narrative are LIVE from risk stratification (latest
 * assessment via history → detail; the narrative is detail-only). Everything
 * else — health score, quick glimpse, "Re-calculate risk" — is still sample
 * content awaiting its own source. The chips are the board's "ghost" variant
 * (h38 / fully-round / no affordance) — deliberately not the qp-chip in
 * sections.scss, which is the h28/radius-8 add-chip with a + glyph.
 */

/** Bridge's level enum, in this board's sentence case. 1 is worst. */
const RISK_LEVEL_LABELS = {
  1: "Life threatening risk",
  2: "Very high risk",
  3: "High risk",
  4: "Moderate risk",
  5: "Low risk",
};

const RISK_LEVEL_CLASSES = {
  1: "is-life-threatening",
  2: "is-very-high",
  3: "is-high",
  4: "is-moderate",
  5: "is-low",
};

/**
 * Sample content for the still-unwired parts of the card. Only `healthScore`
 * remains sample data — glimpse groups are now populated from the four
 * clinical v2 services (chief complaints, diagnosis, hospitalisation,
 * physical activity) via `useGlimpse` below.
 */
export const HEALTH_OVERVIEW_SAMPLE = {
  healthScore: 82,
};

/**
 * Extract a display string for a hospitalisation's cause. Prefers the ICD
 * name (denormalised from api-icd-service) because it reads more
 * naturally than the code; falls back to the free-text description.
 * Empty when nothing usable — caller can filter such rows out.
 */
const causeChip = (h) => {
  const c = (h && h.cause) || {};
  return String(c.icdName || c.description || "").trim();
};

/**
 * Pull the four v2 sections in parallel and derive the glimpse groups.
 * Loading is best-effort: each source is independent, so a section
 * whose service is slow or dead doesn't block the rest. Empty groups
 * are dropped so the render only shows sections that actually have
 * active entries.
 */
const useGlimpse = (patientId) => {
  const cc   = useRef(new ChiefComplaintsService()).current;
  const dx   = useRef(new DiagnosisService()).current;
  const hosp = useRef(new HospitalisationService()).current;
  const act  = useRef(new ActivityService()).current;

  const [state, setState] = useState({
    loading: true,
    complaints: [],
    diagnoses: [],
    hospitalisations: [],
    activities: [],
  });

  // Monotonic request token — bumped on every fetch; each response
  // callback compares its captured token to the current one and
  // silently drops when they differ. Cheaper than a per-call
  // AbortController for a hook that fires four HTTP calls per refresh.
  const seqRef = useRef(0);
  const loadGlimpse = useCallback(() => {
    if (!patientId) {
      setState({ loading: false, complaints: [], diagnoses: [],
                 hospitalisations: [], activities: [] });
      return;
    }
    const mySeq = ++seqRef.current;
    setState((prev) => ({ ...prev, loading: true }));
    let pending = 4;
    const bump = (patch) => {
      if (mySeq !== seqRef.current) return; // superseded — drop.
      setState((prev) => {
        const next = { ...prev, ...patch };
        pending -= 1;
        if (pending <= 0) next.loading = false;
        return next;
      });
    };

    // CC v2 already splits active/past server-side.
    cc.list(patientId,
      (doc) => bump({ complaints: (doc && doc.active) || [] }),
      ()    => bump({ complaints: [] }),
    );
    // Dx v2 too. Diagnosis names live on `.diagnosis`.
    dx.list(patientId,
      (doc) => bump({ diagnoses: (doc && doc.active) || [] }),
      ()    => bump({ diagnoses: [] }),
    );
    // Hospitalisation v2 returns a flat list — filter to status
    // "hospitalised" (the two-state enum: hospitalised | discharged).
    hosp.list(patientId,
      (doc) => bump({
        hospitalisations: ((doc && doc.hospitalisations) || [])
          .filter((h) => String(h && h.status || "").toLowerCase() === "hospitalised"),
      }),
      () => bump({ hospitalisations: [] }),
    );
    // Physical activity v2 also flat — surface anything the patient
    // is still doing at all. Status enum is active / occasional /
    // inactive; the first two mean "part of current routine" and
    // belong on the glimpse. Only "inactive" (fully stopped) is
    // hidden.
    act.list(patientId,
      (doc) => bump({
        activities: ((doc && doc.activities) || [])
          .filter((a) => {
            const s = String(a && a.status || "").toLowerCase();
            return s === "active" || s === "occasional";
          }),
      }),
      () => bump({ activities: [] }),
    );
  }, [patientId, cc, dx, hosp, act]);

  useEffect(() => {
    loadGlimpse();
    // Subscribe to each section's refresh broadcast so an add/update
    // in Chief complaints / Diagnosis / Hospitalisation / Physical
    // activity is reflected on the dashboard card without needing a
    // menu switch. Anthropometric + Lab investigation cards already
    // use this pattern (see AnthroOverviewCard.js — ANTH_V2_REFRESH /
    // LI_V2_REFRESH); this is the missing pair.
    EventService.addListener(CC_V2_REFRESH,   loadGlimpse);
    EventService.addListener(DX_V2_REFRESH,   loadGlimpse);
    EventService.addListener(HOSP_V2_REFRESH, loadGlimpse);
    EventService.addListener(ACT_V2_REFRESH,  loadGlimpse);
    return () => {
      // Bumping the seq invalidates any in-flight responses so a late
      // callback doesn't setState after unmount.
      seqRef.current += 1;
      EventService.removeListener(CC_V2_REFRESH,   loadGlimpse);
      EventService.removeListener(DX_V2_REFRESH,   loadGlimpse);
      EventService.removeListener(HOSP_V2_REFRESH, loadGlimpse);
      EventService.removeListener(ACT_V2_REFRESH,  loadGlimpse);
    };
  }, [loadGlimpse]);

  // Derive the ordered list of glimpse groups. Group order matches the
  // mockup: hospitalisation highlight (unlabelled) → complaints → dx →
  // activity. An empty group is omitted entirely; the render should
  // never draw a labelled section with no chips inside.
  return useMemo(() => {
    if (state.loading) return { loading: true, groups: [] };
    const groups = [];
    // Hospitalisation highlight — one chip per active row, prefixed
    // "Hospitalised-" per the mockup.
    const hospChips = state.hospitalisations
      .map((h) => {
        const name = causeChip(h);
        return name ? `Hospitalised- ${name}` : "";
      })
      .filter(Boolean);
    if (hospChips.length) {
      groups.push({ id: "highlight", label: null, items: hospChips });
    }
    // Chief complaints.
    const ccChips = state.complaints
      .map((c) => String(c && c.complaint || "").trim())
      .filter(Boolean);
    if (ccChips.length) {
      groups.push({ id: "complaints", label: "Chief complaints", items: ccChips });
    }
    // Diagnosis.
    const dxChips = state.diagnoses
      .map((d) => String(d && d.diagnosis || "").trim())
      .filter(Boolean);
    if (dxChips.length) {
      groups.push({ id: "diagnosis", label: "Diagnosis", items: dxChips });
    }
    // Physical activity.
    const actChips = state.activities
      .map((a) => String(a && a.name || "").trim())
      .filter(Boolean);
    if (actChips.length) {
      groups.push({ id: "activity", label: "Physical activity", items: actChips });
    }
    return { loading: false, groups };
  }, [state]);
};

/** The refresh glyph on the "Re-calculate risk" button (icon/refresh, 13×13). */
const RefreshIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path
      d="M11.4 6.2C11.4 9.07 9.07 11.4 6.2 11.4C3.33 11.4 1 9.07 1 6.2C1 3.33 3.33 1 6.2 1C8.05 1 9.68 1.97 10.6 3.42"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path d="M8.4 0.6H12.8V5L8.4 0.6Z" fill="currentColor" />
  </svg>
);
RefreshIcon.propTypes = { className: PropTypes.string };

const HealthOverviewCard = ({ patient }) => {
  const named = [patient && patient.firstName, patient && patient.lastName]
    .filter(Boolean)
    .join(" ");
  const data = HEALTH_OVERVIEW_SAMPLE;
  const patientId = patient && patient.id;
  const service = useRef(new RiskStratificationService()).current;
  const glimpse = useGlimpse(patientId);

  // The latest risk assessment: history (newest-first) picks the record,
  // detail brings the level + AI narrative. `riskSummary` only exists on
  // detail, so both calls are unavoidable.
  const [risk, setRisk] = useState({ state: "loading" });
  const [recalcing, setRecalcing] = useState(false);
  const [recalcError, setRecalcError] = useState("");

  /**
   * The logged-in doctor, which the stratify call is attributed to. An
   * employee-only login carries `doctorProfile.id` 0 (or none), and there is
   * nothing sensible to attribute an assessment to — so the button is disabled
   * rather than firing a request with a 0 in the path.
   */
  const docId = (() => {
    const p = new AuthenticationService().getUserProfile();
    const id = p && p.doctorProfile && p.doctorProfile.id;
    return id > 0 ? id : 0;
  })();

  const loadRisk = useCallback(() => {
    if (!patientId) {
      // No patient, no fetch — land on the honest note, not eternal loading.
      setRisk({ state: "empty" });
      return undefined;
    }
    let cancelled = false;
    setRisk({ state: "loading" });
    service.getHistory(
      patientId,
      (rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows.filter((r) => r && r.id) : [];
        if (!list.length) {
          setRisk({ state: "empty" });
          return;
        }
        // The wire is newest-first today; sort anyway — the order is the
        // backend's, not a contract. NaN-safe: a row with a malformed
        // triggeredAt sorts oldest instead of poisoning the comparator.
        const ts = (r) => {
          const t = new Date(r.triggeredAt).getTime();
          return isNaN(t) ? 0 : t;
        };
        const newest = [...list].sort((a, b) => ts(b) - ts(a))[0];
        service.getDetail(
          newest.id,
          (detail) => {
            if (cancelled) return;
            // An evaluator's corrected level wins over the raw AI one.
            const level =
              (detail && (detail.updatedRiskLevel || detail.riskLevel)) || 0;
            if (!RISK_LEVEL_LABELS[level]) {
              setRisk({ state: "empty" });
              return;
            }
            setRisk({
              state: "loaded",
              level,
              summary: String((detail && detail.riskSummary) || "").trim(),
            });
          },
          () => !cancelled && setRisk({ state: "error" })
        );
      },
      () => !cancelled && setRisk({ state: "error" })
    );
    return () => {
      cancelled = true;
    };
  }, [patientId, service]);

  useEffect(() => {
    loadRisk();
    // Same four broadcasts drive the Patient-health-overview risk
    // block — a new complaint / diagnosis / hospitalisation /
    // activity is exactly the kind of change a risk assessment would
    // reflect, so re-read the latest whenever any of them fire. This
    // is a READ ONLY: no new stratify call, just a fresh
    // getHistory + getDetail.
    EventService.addListener(CC_V2_REFRESH,   loadRisk);
    EventService.addListener(DX_V2_REFRESH,   loadRisk);
    EventService.addListener(HOSP_V2_REFRESH, loadRisk);
    EventService.addListener(ACT_V2_REFRESH,  loadRisk);
    return () => {
      EventService.removeListener(CC_V2_REFRESH,   loadRisk);
      EventService.removeListener(DX_V2_REFRESH,   loadRisk);
      EventService.removeListener(HOSP_V2_REFRESH, loadRisk);
      EventService.removeListener(ACT_V2_REFRESH,  loadRisk);
    };
  }, [loadRisk]);

  /**
   * Trigger a fresh assessment, then re-read it.
   *
   * The stratify endpoint is a GET that MUTATES, so it is fired only from this
   * click — never from an effect, and never retried automatically. The response
   * body is not the new assessment, so the card re-reads history + detail
   * afterwards rather than trying to parse it.
   */
  const recalculate = () => {
    if (!patientId || !docId || recalcing) return;
    setRecalcing(true);
    setRecalcError("");
    service.stratify(
      patientId,
      docId,
      () => {
        setRecalcing(false);
        loadRisk();
      },
      (err) => {
        setRecalcing(false);
        // Say so on the card: a silent no-op is what this button already was.
        setRecalcError((err && err.message) || "Could not re-calculate risk.");
      }
    );
  };

  return (
    <section
      className="po-card"
      aria-label={named ? `Patient health overview for ${named}` : "Patient health overview"}
    >
      <header className="po-head">
        <h2 className="po-title">Patient health overview</h2>
        {/* Health score + Re-calculate risk are HIDDEN for this release
            (2026-08-26) \u2014 the backend scoring + stratify-on-demand
            flows aren't ready yet. The state, service, docId lookup,
            recalcError banner + all handlers below are intentionally
            kept so restoring the affordance is a JSX-only change once
            the backend lands. See git history for the previous JSX. */}
      </header>

      <div className="po-risk-block">
        {risk.state === "loaded" ? (
          <>
            <span className={`po-risk-pill ${RISK_LEVEL_CLASSES[risk.level]}`}>
              {RISK_LEVEL_LABELS[risk.level]}
            </span>
            {risk.summary ? <p className="po-summary">{risk.summary}</p> : null}
          </>
        ) : (
          <p className="po-risk-note">
            {risk.state === "loading"
              ? "Loading risk assessment…"
              : risk.state === "error"
              ? "Couldn't load the risk assessment."
              : "No risk assessment for this patient yet."}
          </p>
        )}
      </div>

      <div className="po-glimpse">
        <p className="po-glimpse-title">Quick glimpse</p>
        {glimpse.loading ? (
          <p className="po-glimpse-note">Loading glimpse…</p>
        ) : glimpse.groups.length === 0 ? (
          <p className="po-glimpse-note">
            No active complaints, diagnoses, hospitalisations or activities
            recorded yet.
          </p>
        ) : (
          glimpse.groups.map((group) => (
            <React.Fragment key={group.id}>
              {group.label ? <p className="po-group-label">{group.label}</p> : null}
              <div className="po-chips">
                {group.items.map((item, i) => (
                  // key includes index — same chip text can legitimately
                  // repeat across two active hospitalisations (e.g. two
                  // "Hospitalised- Fever" rows for two admissions).
                  <span className="po-chip" key={`${group.id}-${i}-${item}`}>
                    {item}
                  </span>
                ))}
              </div>
            </React.Fragment>
          ))
        )}
      </div>
    </section>
  );
};

HealthOverviewCard.propTypes = {
  /**
   * Optional, but the risk block needs `patient.id` — without it the card
   * shows the no-assessment note (the glimpse/score sample still renders).
   */
  patient: PropTypes.object,
};

export default HealthOverviewCard;
